'use client';

/**
 * LoadingState Component
 *
 * Skeleton loading state for the DataTable.
 * Renders row-shaped skeletons to prevent layout shift.
 *
 * Typography uses inline styles to avoid Tailwind arbitrary value conflicts.
 * See tokens.ts for detailed explanation of the Tailwind CSS variable issue.
 */

import React from 'react';
import { themeClasses, defaultTheme, getTextStyles } from './theme/tokens';

interface LoadingStateProps {
  skeletonCount?: number;
  columnCount?: number;
  message?: string;
  density?: 'comfortable' | 'compact';
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  skeletonCount = 5,
  columnCount = 4,
  message,
  density = 'comfortable',
}) => {
  const rowClass = density === 'comfortable'
    ? themeClasses.row.comfortable
    : themeClasses.row.compact;

  // Get typography styles (inline styles to avoid Tailwind conflicts)
  const textStyles = getTextStyles(defaultTheme);

  return (
    <tbody className="bg-white divide-y divide-slate-200">
      {message && (
        <tr>
          <td colSpan={columnCount} className="px-6 py-4 text-center">
            <p style={textStyles.secondary()}>{message}</p>
          </td>
        </tr>
      )}
      {Array.from({ length: skeletonCount }).map((_, rowIndex) => (
        <tr key={rowIndex} className={rowClass}>
          {Array.from({ length: columnCount }).map((_, colIndex) => (
            <td key={colIndex} className={themeClasses.cell}>
              <div className="animate-pulse">
                <div
                  className="h-4 bg-slate-200 rounded"
                  style={{
                    width: `${60 + Math.random() * 40}%`,
                  }}
                />
              </div>
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
};
